import { set, get, merge } from 'lodash';
import moment from 'moment';
import {
  packageStatusColors,
  transferStatusColors,
  defaultDateFormat,
  defaultTimeFormat,
  clientCommands,
} from '../appConstants';
import { objectSelector } from 'theme/utils';

export function isDefined(obj) {
  return obj !== undefined && obj !== null;
}

const filterTypes = {
  equals: (a, b) => a === b,
  contains: (a, b) =>
    isDefined(a) && isDefined(b)
      ? a.toString().indexOf(b.toString()) !== -1
      : false,
};

const addRoutesToURL = (url = '', routes) => {
  if (!routes) return url;
  if (routes instanceof Array) {
    url = url.length
      ? url[url.length - 1] === '/'
        ? url
        : url.concat('/')
      : url;
    for (let index = 0; index < routes.length; index++) {
      const element = routes[index];
      if (isDefinedAndNotEmpty(element))
        url = url.concat(`${encodeURIComponent(element)}/`);
    }
    return url;
  }
  if (typeof routes === 'object') {
    url = '/'.concat(url);
    for (const key in routes) {
      if (routes.hasOwnProperty(key)) {
        const element = routes[key];
        if (isDefinedAndNotEmpty(element))
          url = url.concat(`${encodeURIComponent(element)}/`);
      }
    }
    return url;
  }
  return url;
};

const addQueryToURL = (url = '', queryObject = {}) => {
  let queryAdded = false;
  for (const key in queryObject) {
    if (queryObject.hasOwnProperty(key)) {
      const element = queryObject[key];
      if (isDefinedAndNotEmpty(element)) {
        if (!queryAdded) {
          url = url.concat('?');
        }
        url = url.concat(
          `${queryAdded ? '&' : ''}${encodeURIComponent(
            key
          )}=${encodeURIComponent(element)}`
        );
        queryAdded = true;
      }
    }
  }
  return url;
};

function objectFilter(item, filterObject, filterOptions) {
  for (const key in filterObject) {
    if (filterObject.hasOwnProperty(key)) {
      const element = filterObject[key];
      if (
        (item === undefined && element === undefined) ||
        (item === null && element === null)
      )
        continue;
      if (!item.hasOwnProperty(key)) {
        return false;
      }
      if (typeof element === 'object') {
        if (!objectFilter(item[key], element)) return false;
      } else {
        let filter = filterTypes.equals;
        if (filterOptions) {
          filter = filterOptions.find((filter) => filter.name === key);
          filter =
            filter && filter.filterType
              ? filter.filterType
              : filterTypes.equals;
        }
        if (!filter(item[key], element)) return false;
      }
    }
  }
  return true;
}

export function isDefinedAndNotEmpty(obj) {
  if (typeof obj === 'string')
    return obj !== undefined && obj !== null && obj !== '';
  else if (Array.isArray(obj))
    return obj !== undefined && obj !== null && obj.length;
  else return isDefined(obj);
}

export function createURL(url, routes, queryObject) {
  return addQueryToURL(
    addRoutesToURL(
      url,
      typeof routes === 'string' ? routes.split('/') : routes
    ),
    queryObject
  );
}

export function serializeObjectWithDot(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  let serializedObject = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const element = obj[key];
      const isDotted = key.indexOf('.') !== -1;
      if (isDotted) set(serializedObject, key, element);
      else serializedObject[key] = element;
    }
  }
  return serializedObject;
}

export function getObjectFromString(valuePath, obj) {
  return get(obj, valuePath);
}

export function setObjectFromString(obj, valuePath, value) {
  return set(obj, valuePath, value);
}

export function filterByObject(data, filterObject, filterOptions) {
  if (!data || !filterObject) return data;
  return data.filter((item) => objectFilter(item, filterObject, filterOptions));
}

export function mergeObjects(firstObj, secondObj) {
  return merge(firstObj, secondObj);
}

export function getRandom(max = 10000) {
  return (new Date().getTime() * Math.random()) % (max + 1);
}

export function shallowEqual(objA, objB) {
  if (objA === objB) {
    return true;
  }

  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  var keysA = Object.keys(objA);
  var keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  // Test for A's keys different from B.
  var bHasOwnProperty = Object.prototype.hasOwnProperty.bind(objB);
  for (var i = 0; i < keysA.length; i++) {
    if (!bHasOwnProperty(keysA[i]) || objA[keysA[i]] !== objB[keysA[i]]) {
      return false;
    }
  }

  return true;
}

export function arrayEqual(arrA, arrB) {
  if (arrA === arrB) {
    return true;
  }

  if (!(arrA instanceof Array) || !(arrB instanceof Array)) {
    return false;
  }
  if (arrA.length !== arrB.length) {
    return false;
  }
  for (let i = 0; i < arrA.length; i++) {
    // eslint-disable-next-line
    if (arrA[i] != arrB[i]) {
      return false;
    }
  }

  return true;
}

export function dateFormatter(dateObject = new Date(), timeFormatting = false) {
  if (!isDefinedAndNotEmpty(dateObject)) return '';
  if (timeFormatting)
    return moment(dateObject).format(
      defaultDateFormat + ' ' + defaultTimeFormat
    );
  return moment(dateObject).format(defaultDateFormat);
}

export function getStatusColor(stateId, type) {
  switch (type) {
    case 'package': {
      const statusColor = packageStatusColors.find(
        (item) => item.stateId === stateId
      );
      if (statusColor) return statusColor.value;
      break;
    }
    case 'transfer': {
      const statusColor = transferStatusColors.find(
        (item) => item.stateId === stateId
      );
      if (statusColor) return statusColor.value;
      break;
    }
    default:
      break;
  }
}

export const copyClipboard = (text) => {
  const event = document.createElement('textarea');
  event.value = text;
  document.body.appendChild(event);
  event.select();
  document.execCommand('copy');
  document.body.removeChild(event);
};

export const isCommandAvailable = (command) => {
  return clientCommands.some((action) => action.command === command);
};

export const hasAnyKey = (object) => {
  return Object.keys(object).length > 0;
};

export const maskName = (name) => {
  if (!name) return '';

  let customerSurname = name[0];
  for (let i = 0; i < name.length - 2; i++) {
    customerSurname += '*';
  }
  return customerSurname + name[name.length - 1];
};

export const groupPackagesByMerchant = (items) => {
  return items.reduce((acc, currentItem) => {
    for (const item of currentItem.packageitem_set) {
      const {
        epim_merchGroupCode,
        epim_merchSubGroupCode,
        integration_merchandisingGroupCode,
        integration_merchandisingSubGroup,
        merchandisingGroupCode,
        merchandisingSubGroup,
      } = item.order_item.product.attributes;

      const groupCode =
        epim_merchGroupCode ||
        integration_merchandisingGroupCode ||
        merchandisingGroupCode;

      const subGroupCode =
        epim_merchSubGroupCode ||
        integration_merchandisingSubGroup ||
        merchandisingSubGroup;

      // Boş gelen attribute değeri olması durumunda
      const groupName = groupCode ? `${groupCode}-${subGroupCode}` : '';
      // group items
      const { order_number, number } = currentItem;
      acc[groupName] = [
        { item, order_number, number, subGroupCode },
        ...(acc[groupName] || []),
      ];
    }

    return acc;
  }, {});
};

export const isAuthorized = () => {
  let authorizedPaths = [
    '/store-selection',
    '/store-selection?',
    '/login',
    '/staff-selection',
    '/staff-login',
  ];
  let pathControl = authorizedPaths.includes(window.location.pathname);

  let isStoreAndStaffLogged =
    JSON.parse(localStorage.getItem('storeLoggedIn')) &&
    JSON.parse(localStorage.getItem('staffLoggedIn'));

  if (!pathControl && !isStoreAndStaffLogged) {
    return false;
  } else {
    return true;
  }
};

export const handleStarterPages = () => {
  let paths = ['/staff-selection', '/staff-login'];

  let isStaffLogged = JSON.parse(localStorage.getItem('staffLoggedIn'));

  let notPermitted = paths.includes(window.location.pathname);

  if (!isStaffLogged && notPermitted) {
    return true;
  } else {
    return false;
  }
};

export const clearStockLocationId = () => {
  localStorage.removeItem('stockLocationId');
};

export const getNestedTPP = (obj, path) => {
  const args = path.split('.');
  return args.reduce((obj, level) => obj && obj[level], obj);
};

export function remove(arr, item) {
  var index = arr.indexOf(item);
  return [...arr.slice(0, index), ...arr.slice(index + 1)];
}

/***
 * return chunked array
 * @example chunkArray([1,2,3,4,5,6], 2);
 * @return [[1,2],[3,4],[5,6]]
 *
 * @firstArgument {Array}
 * @otherArguments {Number}
 * @returns {Array}
 */

export const chunkArray = (array = [], chunkCount = 2) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkCount) {
    chunks.push(array.slice(i, i + chunkCount));
  }
  return chunks;
};

export function priceFormatter({ price, locale = 'tr-Tr', currency = 'TRY' }) {
  let formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    minimumFractionDigits: 2,
    currency,
  }).format(price);

  return formatted.slice(1) + formatted.slice(0, 1);
}

export const getLocalItem = (item) => {
  const localItem = localStorage.getItem(`${item}`);
  if (!!localItem && !['undefined', 'null'].includes(String(localItem)))
    return localItem;

  return null;
};

export const checkAndGetLocalItem = (localItem, defaultValue) => {
  try {
    return getLocalItem(`${localItem}`)
      ? JSON.parse(getLocalItem(`${localItem}`))
      : defaultValue;
  } catch (_) {
    return defaultValue;
  }
};

export const getStoreRemoteId = () => {
  const currentStoreRemoteId = objectSelector(
    checkAndGetLocalItem('localCurrentStaff'),
    'current_store.remote_id'
  );
  const fallbackStoreRemoteId = objectSelector(
    checkAndGetLocalItem('usedStorage'),
    'remote_id'
  );
  const storeRemoteId = currentStoreRemoteId || fallbackStoreRemoteId || '';

  return storeRemoteId;
};



// WEBPACK FOOTER //
// ./src/utils/index.js